import React from "react";

import { cn } from "../../lib/utils";
import { Skeleton } from "../ui/skeleton";

const getFieldTone = (percent) => {
  if (percent >= 85) {
    return {
      text: "text-emerald-300",
      bar: "bg-emerald-400",
      pill: "border-emerald-400/20 bg-emerald-400/10 text-emerald-300",
      label: "Healthy",
    };
  }

  if (percent >= 60) {
    return {
      text: "text-amber-300",
      bar: "bg-amber-300",
      pill: "border-amber-400/20 bg-amber-400/10 text-amber-300",
      label: "In flight",
    };
  }

  return {
    text: "text-zinc-300",
    bar: "bg-zinc-400",
    pill: "border-zinc-700 bg-zinc-800/80 text-zinc-300",
    label: "Sparse",
  };
};

const FieldRow = ({ field, sampledCount }) => {
  const tone = getFieldTone(field.coveragePercent);

  return (
    <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-['Outfit'] text-zinc-50">{field.label}</p>
            <div className={cn("rounded-full border px-2.5 py-1 text-[11px] font-mono uppercase tracking-[0.18em]", tone.pill)}>
              {tone.label}
            </div>
          </div>
          <p className="mt-2 text-sm font-['Manrope'] text-zinc-500">{field.description}</p>
        </div>
        <div className="flex-shrink-0 text-right">
          <p className={cn("font-mono text-lg", tone.text)}>{field.coveragePercent}%</p>
          <p className="text-xs font-mono text-zinc-500">
            {field.populatedCount.toLocaleString()} / {sampledCount.toLocaleString()}
          </p>
        </div>
      </div>

      <div className="mt-4 h-2 rounded-full bg-zinc-800">
        <div
          className={cn("h-2 rounded-full transition-all duration-300", tone.bar)}
          style={{ width: field.coveragePercent > 0 ? `${Math.max(field.coveragePercent, 3)}%` : "0%" }}
        />
      </div>
    </div>
  );
};

const LoadingRows = () => (
  <div className="space-y-3">
    {Array.from({ length: 4 }).map((_, index) => (
      <div key={index} className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-28 bg-white/10" />
            <Skeleton className="h-3 w-52 bg-white/10" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-12 bg-white/10" />
            <Skeleton className="h-3 w-16 bg-white/10" />
          </div>
        </div>
        <Skeleton className="mt-4 h-2 w-full rounded-full bg-white/10" />
      </div>
    ))}
  </div>
);

const EmptyState = () => (
  <div className="rounded-2xl border border-dashed border-zinc-800 bg-zinc-950/40 px-4 py-10 text-center">
    <p className="font-['Outfit'] text-zinc-50">No normalization stats yet</p>
    <p className="mt-2 text-sm font-['Manrope'] text-zinc-500">
      English-field progress will appear once the startup sample is available.
    </p>
  </div>
);

const NormalizationPanel = ({ normalization, loading }) => {
  const sampledCount = normalization?.sampledCount || 0;
  const overallCoveragePercent = normalization?.overallCoveragePercent || 0;
  const fields = normalization?.fields || [];

  return (
    <section className="rounded-3xl border border-zinc-800 bg-zinc-900/70 p-5 shadow-[0_1px_0_rgba(255,255,255,0.02)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Normalization</p>
          <h2 className="mt-2 font-['Outfit'] text-xl text-zinc-50">English field progress</h2>
        </div>
        <div className="text-right">
          <p className="font-mono text-2xl text-zinc-50">{overallCoveragePercent}%</p>
          <p className="text-xs font-mono uppercase tracking-[0.18em] text-zinc-500">overall fill rate</p>
        </div>
      </div>

      <p className="mt-3 text-sm font-['Manrope'] text-zinc-500">
        Population coverage for the canonical startup fields operators need most often in English.
      </p>

      <div className="mt-5">
        {loading ? (
          <LoadingRows />
        ) : fields.length > 0 && sampledCount > 0 ? (
          <div className="space-y-3">
            {fields.map((field) => (
              <FieldRow key={field.key} field={field} sampledCount={sampledCount} />
            ))}
          </div>
        ) : (
          <EmptyState />
        )}
      </div>
    </section>
  );
};

export default NormalizationPanel;
